import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { MainLayout } from './components/layout/MainLayout';
import { CamerasPage } from './pages/CamerasPage';
import { VideoWallPage } from './pages/VideoWallPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AlertsPage } from './pages/AlertsPage';
import { SettingsPage } from './pages/SettingsPage';
import { JunctionMonitoringPage } from './pages/JunctionMonitoringPage';
import { DarshanEvidencePage } from './pages/DarshanEvidencePage';
import { NetworkCommandPage } from './pages/NetworkCommandPage';
import { OperationsPage } from './pages/OperationsPage';
import { QLinesPage } from './pages/QLinesPage';
import { OverviewPage } from './pages/OverviewPage';
import { TrainsPage } from './pages/TrainsPage';
import { HoldingAreaView } from './Demoportal/HoldingAreaView';
import { HoldingMappingPage } from './pages/HoldingMappingPage';
import { SectorReadinessView } from './Demoportal/SectorReadinessView';
import { MainTempleRoomView } from './Demoportal/MainTempleRoomView';
import { AntharalayamCamerasPage } from './pages/AntharalayamCamerasPage';
import { QueueOperationsView } from './Demoportal/QueueOperationsView';
import { DigitalTwinView } from './Demoportal/DigitalTwinView';
import { useEffect, useState, useRef } from 'react';
import { checkAuthSession, LoginPage } from './pages/LoginPage';

import { OverviewPage as OverviewClassicPage } from './pages/OverviewClassicPage';
import { TopBar } from './components/layout/TopBar';
import { DirectionPage } from './pages/DirectionPage';
// 16 Oct 2026: loaded only when /command-centre is opened - it carries 8 MB of pictures (pages/kanakaAssets.ts) that
// were part of every page's download and made the whole portal slow to open
const KanakaDurgaCommandCentrePage = lazy(() => import('./pages/KanakaDurgaCommandCentrePage').then(m => ({ default: m.KanakaDurgaCommandCentrePage })));

import { bootstrapAuth, type AuthMode } from './auth/auth';
import DarshanPathsPage from './pages/DarshanPathsPage';
import { PublicDarshanPathpage } from './pages/PublicDarshanPathpage';


function App() {
  // Public routes: accessible immediately for digital signage, kiosks & public dashboards
  const pathname = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
  const isDirectDisplay =
    pathname === '/direction' ||
    pathname === '/direction/' ||
    pathname === '/directions' ||
    pathname === '/direction.html' ||
    pathname.includes('darshan_hoarding_display');

  const isPublicDarshan =
    pathname === '/darshan-timings/public' ||
    pathname === '/darshan-timings/public/' ||
    pathname.startsWith('/darshan-timings/public');

  const isPublicRoute = isDirectDisplay || isPublicDarshan;

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => checkAuthSession());

  // Which sign-in this server uses: Keycloak (main AI server) or the built-in login (mini / Spark).
  // 'loading' until /api/auth/config answers; 'error' while the server cannot be reached (keeps retrying).
  const [authMode, setAuthMode] = useState<AuthMode | 'loading' | 'error'>('loading');
  const errSince = useRef<number | null>(null);      // a restart is not reported: only a server unreachable for 2 minutes
  useEffect(() => {
    if (isPublicRoute) return; // Public display: never initiate sign-in / Keycloak redirects

    let alive = true;
    let timer: number | undefined;
    const attempt = () => {
      bootstrapAuth()
        .then(mode => { if (alive) { errSince.current = null; setAuthMode(mode); } })
        .catch(() => {
          if (!alive) return;
          setAuthMode('error');
          if (errSince.current === null) errSince.current = Date.now();
          timer = window.setTimeout(attempt, 5000);
        });
    };
    attempt();
    return () => { alive = false; window.clearTimeout(timer); };
  }, [isPublicRoute]);

  // Built-in login only: check the 45-minute session & listen for logout events
  useEffect(() => {
    if (isPublicRoute) return;
    if (authMode !== 'local') return;
    const handleAuthChange = () => {
      setIsAuthenticated(checkAuthSession());
    };

    window.addEventListener('auth:logout', handleAuthChange);

    if (!isAuthenticated) {
      return () => window.removeEventListener('auth:logout', handleAuthChange);
    }

    const interval = setInterval(() => {
      const isValid = checkAuthSession();
      if (!isValid) {
        setIsAuthenticated(false);
      }
    }, 15000); // Check every 15 seconds

    return () => {
      window.removeEventListener('auth:logout', handleAuthChange);
      clearInterval(interval);
    };
  }, [isAuthenticated, authMode, isPublicRoute]);

  if (isDirectDisplay) {
    return (
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="*" element={<DirectionPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  if (isPublicDarshan) {
    return (
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="*" element={<PublicDarshanPathpage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      {authMode === 'loading' || authMode === 'error' ? (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#f4f5f8] dark:bg-[#111215] text-neutral-600 dark:text-neutral-300">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-300 dark:border-white/15 border-t-blue-500 animate-spin" />
          <div className="text-sm font-semibold">
            {authMode === 'error' && errSince.current !== null && Date.now() - errSince.current > 120000 ? 'Cannot reach the server — retrying…' : 'Checking sign-in…'}
          </div>
        </div>
      ) : authMode === 'local' && !isAuthenticated ? (
        <LoginPage onLoginSuccess={() => setIsAuthenticated(true)} />
      ) : (
        <BrowserRouter>
          <Routes>
            {/* Standalone Public Darshan Paths Page: TopBar included, MacDock excluded */}
            <Route path="/darshan-timings/public" element={<PublicDarshanPathpage />} />

            {/* Standalone Direction / Hoarding Display */}
            <Route path="/direction" element={<DirectionPage />} />
            <Route path="/directions" element={<Navigate to="/direction" replace />} />
            <Route path="/direction.html" element={<Navigate to="/direction" replace />} />

            {/* Standalone Command Centre: TopBar included, sidebar dock excluded */}
            <Route
              path="/command-centre"
              element={
                <div className="min-h-screen flex flex-col bg-[#FBF9F5] dark:bg-[#111215] text-neutral-800 dark:text-neutral-100 transition-colors duration-200">
                  <TopBar />
                  <main className="flex-1 w-full mx-auto px-2 sm:px-4 py-2.5 max-w-[1720px]">
                    <Suspense fallback={<div className="p-6 text-sm text-neutral-500">Loading the Command Centre…</div>}>
                      <KanakaDurgaCommandCentrePage />
                    </Suspense>
                  </main>
                </div>
              }
            />
            <Route path="/command-center" element={<Navigate to="/command-centre" replace />} />
            <Route path="/kanaka-durga-command-centre" element={<Navigate to="/command-centre" replace />} />
            <Route path="/kanaka-durga" element={<Navigate to="/command-centre" replace />} />

            <Route path="/" element={<MainLayout />}>
              {/* after sign-in (and for the bare address) the console opens on the Overview */}
              <Route index element={<Navigate to="/overview" replace />} />
              {/* the Dashboard page is hidden from the portal (11 Oct 2026): its address opens the Overview. The page itself
                  is kept in pages/DashboardPage.tsx */}
              <Route path="dashboard" element={<Navigate to="/overview" replace />} />

              <Route path="overview2" element={<Navigate to="/overview" replace />} />
              <Route path="overview-classic" element={<OverviewClassicPage />} />

              <Route path="overview" element={<OverviewPage />} />
              <Route path="trains" element={<TrainsPage />} />

              <Route path="operations" element={<OperationsPage />} />
              <Route path="network-command" element={<NetworkCommandPage />} />
              <Route path="junctions" element={<JunctionMonitoringPage />} />
              <Route path="darshan-timings" element={<DarshanPathsPage />} />
              <Route path="darshan-evidence" element={<DarshanEvidencePage />} />
              <Route path="qlines" element={<QLinesPage />} />
              <Route path="cameras" element={<CamerasPage />} />
              <Route path="video-wall" element={<VideoWallPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="holding-area" element={<HoldingAreaView />} />
              <Route path="holding-mapping" element={<HoldingMappingPage />} />
              <Route path="sector-readiness" element={<SectorReadinessView />} />
              <Route path="main-templeroom" element={<MainTempleRoomView />} />
              <Route path="antharalayam-cameras" element={<AntharalayamCamerasPage />} />
              <Route path="queue-operations" element={<QueueOperationsView />} />
              <Route path="digital-twin" element={<DigitalTwinView />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      )}
    </ThemeProvider>
  );
}

export default App;
